import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Spin, Result, Button, Typography, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  HomeOutlined,
  CreditCardOutlined,
  SettingOutlined,
  MessageOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { fetchSubInfo } from "../../../store/slices/auth.slice";
import subService from "../../../services/sub/sub.service";
import smsService from "../../../services/sms/sms.service";
import { all_routes } from "../../../router/all_routes";

const { Title, Text } = Typography;


interface SMSPaymentVerificationResult {
  status: "success" | "error";
  message: string;
  newSmsLimit?: number;
  smsPackage: {
    id: number;
    name: string;
    description: string;
    smsAmount: number;
    price: number;
    isActive: boolean;
    status: string;
    referenceCode: string;
    purchaseDate: string;
    expiryDate?: string;
  };
}

const SMSCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<SMSPaymentVerificationResult | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // SMS callback işleminin sadece bir kez çalışmasını sağlamak için - basit çözüm

  useEffect(() => {
    const verifySMSPayment = async () => {
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);

      // --- 1) Paramları tek sefer snapshot al
      const urlToken = searchParams.get("token");
      const lsToken = localStorage.getItem("sms_checkoutsession") || undefined;
      const conversationId = localStorage.getItem("sms_conversationId") || undefined;


      try {
        // Token kontrolü
        if (!urlToken && !lsToken) {

          setResult({
            status: "error",
            message: "SMS paketi ödeme doğrulama işlemi başarısız oldu. Lütfen SMS satın alma işlemini tekrar başlatın.",
            smsPackage: {
              id: 0,
              name: "Bilinmeyen Paket",
              description: "Paket bilgisi alınamadı",
              smsAmount: 0,
              price: 0,
              isActive: false,
              status: "error",
              referenceCode: "",
              purchaseDate: "",
            },
          });
          setLoading(false);
          setIsProcessing(true);
          return;
        }

        const token = urlToken || lsToken;

        // ConversationId eksik kontrol
        if (!conversationId) {

           setResult({
             status: "error",
             message: "SMS ödeme doğrulama işlemi başarısız oldu. Lütfen SMS satın alma işlemini tekrar başlatın.",
             smsPackage: {
               id: 0,
               name: "Bilinmeyen Paket",
               description: "Paket bilgisi alınamadı",
               smsAmount: 0,
               price: 0,
               isActive: false,
               status: "error",
               referenceCode: "",
               purchaseDate: "",
             },
           });
          setLoading(false);
          setIsProcessing(true);
          return;
        }

      
        
        const apiCallStartTime = performance.now();
        const verificationResult = await smsService.verifyPurchase(token!, conversationId!);
        const apiCallEndTime = performance.now();
        // 🔒 BAŞARILI DOĞRULAMA: SMS Token'ları temizle (başarılı işlem durumunda)
        // API direkt success data döndürüyor, hata durumunda exception atıyor
        // --- 4) API çağrısı başarı kontrolü
        const isSuccessful = !!(verificationResult.message && verificationResult.newSmsLimit && verificationResult.purchasedPackage);
      

        if (isSuccessful) {

          // Response'u state'e kaydet - Gerçek API response yapısına uygun
          const purchasedPackage = verificationResult.purchasedPackage;

          const uiStateData = {
            status: "success" as "success" | "error", // API başarılı response döndürdüyse success
            message: verificationResult.message || "SMS paketi satın alma işlemi tamamlandı",
            newSmsLimit: verificationResult.newSmsLimit,
            smsPackage: {
              id: 0, // API'de ID yok, varsayılan değer
              name: purchasedPackage?.name || "SMS Paketi",
              description: "SMS paketi açıklaması", // API'de description yok
              smsAmount: purchasedPackage?.smsAmount || 0,
              price: parseFloat(purchasedPackage?.price || "0"),
              isActive: true, // Başarılı olduğu için aktif
              status: "active",
              referenceCode: "", // API'de referenceCode yok
              purchaseDate: new Date().toISOString(),
            },
          };
          
          
          setResult(uiStateData);
          setIsProcessing(true);
          // Token'ları temizle

          const beforeCleanup = {
            sms_checkoutsession: !!localStorage.getItem("sms_checkoutsession"),
            sms_conversationId: !!localStorage.getItem("sms_conversationId")
          };
          
          localStorage.removeItem("sms_checkoutsession");
          localStorage.removeItem("sms_conversationId");
          
          const afterCleanup = {
            sms_checkoutsession: !!localStorage.getItem("sms_checkoutsession"),
            sms_conversationId: !!localStorage.getItem("sms_conversationId")
          };

          setIsProcessing(true);
          // sub info refresh + countdown aynı

          // Mevcut kullanıcı için sub bilgilerini yenile (SMS bilgileri de dahil)
        //   const user = JSON.parse(localStorage.getItem("user") || "{}");
        //   console.log(
        //     "%c👤 [USER INFO] Kullanıcı bilgisi kontrol ediliyor:",
        //     "color: #00BCD4; font-weight: bold;",
        //     {
        //       hasUser: !!user,
        //       hasCompanyId: !!user.companyId,
        //       companyId: user.companyId,
        //       willRefreshSubInfo: !!user.companyId
        //     }
        //   );
          
        //   if (user.companyId) {
        //     console.log(
        //       "%c🔄 [SUB INFO REFRESH] Sub bilgileri yenileniyor...",
        //       "color: #00BCD4; font-weight: bold;"
        //     );
        //     dispatch(fetchSubInfo(user.companyId));
        //   }

          // 5 saniye countdown başlat (SMS için biraz daha uzun)

          setIsRedirecting(true);
        //   setCountdown(5);

        //   const countdownInterval = setInterval(() => {
        //     setCountdown((prev) => {
        //       if (prev <= 1) {
        //         console.log(
        //           "%c🚀 [YÖNLENDİRME] SMS ayarları sayfasına yönlendiriliyor...",
        //           "color: #795548; font-weight: bold;"
        //         );
        //         clearInterval(countdownInterval);
        //         // SMS ayarları sayfasına yönlendir
        //         handleGoToSMSSettings();
        //         return 0;
        //       }
        //       console.log(
        //         "%c⏰ [COUNTDOWN] Yönlendirme countdown:",
        //         "color: #795548;",
        //         `${prev - 1} saniye kaldı`
        //       );
        //       return prev - 1;
        //     });
        //   }, 1000);
        } else {

          setResult({
            status: "error",
            message: verificationResult.message || "SMS paketi satın alma işlemi başarısız oldu.",
            smsPackage: {
              id: 0,
              name: "Hata",
              description: "SMS paketi işlemi başarısız",
              smsAmount: 0,
              price: 0,
              isActive: false,
              status: "error",
              referenceCode: "",
              purchaseDate: "",
            },
          });
          setIsProcessing(true);
        }
      } catch (error: any) {

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "SMS paketi satın alma sırasında bir hata oluştu.";


        setResult({
          status: "error",
          message: errorMessage,
          smsPackage: {
            id: 0,
            name: "Hata",
            description: "SMS paketi işlemi başarısız",
            smsAmount: 0,
            price: 0,
            isActive: false,
            status: "error",
            referenceCode: "",
            purchaseDate: "",
          },
        });
        setIsProcessing(true);
      } finally {

        setLoading(false);
        setIsProcessing(true);
      }
    };

    verifySMSPayment();
  }, []); 

  const handleGoToDashboard = () => {

    navigate(all_routes.adminDashboard);
  };

  const handleGoToSMSSettings = () => {

    navigate(all_routes.companySettings + "?tab=sms-settings");
  };

  const handleRetryPayment = () => {

    navigate(all_routes.companySettings + "?tab=sms-settings");
  };

  const getSMSPackageDisplayName = (name: string, smsAmount: number) => {
    // Eğer name varsa onu kullan, yoksa SMS miktarına göre isim oluştur
    if (name && name !== "SMS Paketi" && name !== "Bilinmeyen Paket") {
      return name;
    }
    return `${smsAmount.toLocaleString()} SMS Paketi`;
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Card style={{ maxWidth: 500, width: "100%", textAlign: "center" }}>
              <div style={{ padding: "40px 20px" }}>
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 48, color: "#52c41a" }}
                      spin
                    />
                  }
                  size="large"
                />
                <div style={{ marginTop: 24 }}>
                  <Title level={4} style={{ color: "#52c41a" }}>
                    <MessageOutlined style={{ marginRight: 8 }} />
                    SMS Paketi Doğrulanıyor...
                  </Title>
                  <Text type="secondary">
                    SMS paketi satın alma işleminiz doğrulanıyor, lütfen bekleyin...
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Card style={{ maxWidth: 600, width: "100%" }}>
            {result?.status === "success" ? (
              <Result
                icon={
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", fontSize: 72 }}
                  />
                }
                title={
                  <Title
                    level={3}
                    style={{ color: "#52c41a", marginBottom: 16 }}
                  >
                    SMS Paketiniz Başarıyla Satın Alındı!
                  </Title>
                }
                subTitle={
                  <div>
                    {/* <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {result.message}
                    </Text> */}
                    
                    <div style={{ 
                      margin: "16px 0",
                      padding: "16px",
                      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                      borderRadius: "12px",
                      border: "1px solid #0ea5e9"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                        <MessageOutlined style={{ color: "#0ea5e9", fontSize: "18px", marginRight: "8px" }} />
                        <Text strong style={{ color: "#0ea5e9", fontSize: "16px" }}>
                          Paket Detayları
                        </Text>
                      </div>
                      
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                        {/* <Tag color="blue" style={{ fontSize: "14px", padding: "6px 12px" }}>
                          📦 {getSMSPackageDisplayName(result.smsPackage.name, result.smsPackage.smsAmount)}
                        </Tag> */}
                        <Tag color="green" style={{ fontSize: "14px", padding: "6px 12px" }}>
                          {result.smsPackage.smsAmount.toLocaleString()} SMS
                        </Tag>
                        <Tag color="orange" style={{ fontSize: "14px", padding: "6px 12px" }}>
                          {result.smsPackage.price.toLocaleString()} ₺
                        </Tag>
                        {/* <Tag color="purple" style={{ fontSize: "14px", padding: "6px 12px" }}>
                          🏷️ {result.smsPackage.referenceCode}
                        </Tag> */}
                      </div>
                      
                      {result.smsPackage.description && result.smsPackage.description !== "SMS paketi açıklaması" && (
                        <div style={{ marginTop: "12px", padding: "8px", background: "#f0f9ff", borderRadius: "6px" }}>
                          <Text style={{ fontSize: "13px", color: "#0ea5e9", fontStyle: "italic" }}>
                            📝 {result.smsPackage.description}
                          </Text>
                        </div>
                      )}
                      
                      {/* <div style={{ marginTop: "12px" }}>
                        <Text style={{ fontSize: "12px", color: "#64748b" }}>
                          📅 Satın Alma: {new Date(result.smsPackage.purchaseDate).toLocaleDateString('tr-TR')}
                        </Text>
                        {result.smsPackage.expiryDate && (
                          <Text style={{ fontSize: "12px", color: "#64748b", marginLeft: "16px" }}>
                            ⏰ Geçerlilik: {new Date(result.smsPackage.expiryDate).toLocaleDateString('tr-TR')}
                          </Text>
                        )}
                      </div> */}
                    </div>

                    <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 12,
                        color: "#52c41a",
                        fontWeight: 600,
                      }}
                    >
                      SMS paketiniz hesabınıza başarıyla eklendi!
                    </Text>
                    {result.newSmsLimit && (
                      <Text
                        style={{
                          fontSize: 14,
                          display: "block",
                          marginBottom: 8,
                          color: "#1890ff",
                          fontWeight: 500,
                        }}
                      >
                        🎉 Yeni SMS bakiyeniz: {result.newSmsLimit.toLocaleString()} SMS
                      </Text>
                    )}
                    {/* <Text
                      style={{
                        fontSize: 14,
                        display: "block",
                        marginBottom: 8,
                        color: "#1890ff",
                      }}
                    >
                      Artık çalışanlarınıza SMS gönderebilirsiniz.
                    </Text> */}
                    {/* {isRedirecting && (
                      <div
                        style={{
                          marginTop: 20,
                          padding: 16,
                          background: "#f0f9ff",
                          borderRadius: 8,
                          border: "1px solid #52c41a",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 16,
                            display: "block",
                            marginBottom: 8,
                            color: "#52c41a",
                            fontWeight: 600,
                          }}
                        >
                          🔄 SMS ayarları sayfasına yönlendiriliyorsunuz...
                        </Text>
                        <Text style={{ fontSize: 14, color: "#666" }}>
                          {countdown} saniye sonra otomatik olarak SMS ayarları sayfasına yönlendirileceksiniz.
                        </Text>
                      </div>
                    )} */}
                  </div>
                }
                extra={
                  !isRedirecting
                    ? [
                        <Button
                          type="primary"
                          key="sms-settings"
                          icon={<MessageOutlined />}
                          onClick={handleGoToSMSSettings}
                          size="large"
                          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                        >
                          SMS Ayarları
                        </Button>,
                        <Button
                          key="dashboard"
                          icon={<HomeOutlined />}
                          onClick={handleGoToDashboard}
                        >
                          Ana Sayfaya Git
                        </Button>,
                      ]
                    : [
                        <Button
                          key="manual-redirect"
                          type="primary"
                          icon={<MessageOutlined />}
                          onClick={handleGoToSMSSettings}
                          size="large"
                          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                        >
                          Hemen SMS Ayarlarına Git
                        </Button>,
                      ]
                }
              />
            ) : (
              <Result
                icon={
                  <CloseCircleOutlined
                    style={{ color: "#ff4d4f", fontSize: 72 }}
                  />
                }
                title={
                  <Title
                    level={3}
                    style={{ color: "#ff4d4f", marginBottom: 16 }}
                  >
                    {result?.status === "error"
                      ? "SMS Paketi Satın Alınamadı"
                      : "SMS Ödeme Başarısız"}
                  </Title>
                }
                subTitle={
                  <div>
                    {/* <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      { "SMS paketi satın alma işlemi tamamlanamadı."}
                    </Text> */}
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 14,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {result?.status === "error"
                        ? "Lütfen kart bilgilerinizi kontrol edip tekrar deneyin."
                        : "Bir hata oluştu, lütfen tekrar deneyin."}
                    </Text>
                    <div style={{ 
                      marginTop: 16,
                      padding: 16,
                      background: "#fff2f0",
                      borderRadius: 8,
                      border: "1px solid #ffccc7"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                        <PhoneOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                        <Text strong style={{ color: "#ff4d4f" }}>
                          Destek Gerekiyor mu?
                        </Text>
                      </div>
                      <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                        SMS paketi satın alma konusunda yardıma ihtiyacınız varsa destek ekibimizle iletişime geçin.
                      </Text>
                    </div>
                  </div>
                }
                extra={[
                  <Button
                    type="primary"
                    key="retry"
                    icon={<CreditCardOutlined />}
                    onClick={handleRetryPayment}
                    size="large"
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                  >
                    Tekrar Dene
                  </Button>,
                  <Button
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                  >
                    Ana Sayfaya Git
                  </Button>,
                ]}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SMSCallback;
